import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { map as _map, flatten } from 'lodash';
import { Observable, forkJoin, map } from 'rxjs';

import { Proposal } from '@lockton-apps/shared/interfaces';
import {
  Policy,
  PolicyCreate,
  PolicyUpdate,
} from '@lockton-apps/shared/interfaces';
import { PolicyHelpers } from '@lockton-apps/shared/util/helpers';

@Injectable({
  providedIn: 'root',
})
export class PoliciesService {
  model = 'policy';

  constructor(
    @Inject('BASE_API_URL') private baseUrl: string,
    private http: HttpClient
  ) {}

  private _getUrl() {
    return `${this.baseUrl}/${this.model}`;
  }

  private _getRecentlyViewedUrl() {
    return `${this._getUrl()}/recently-viewed`;
  }

  private _getUrlForClient(clientId: number) {
    return `${this._getUrl()}/client/${clientId}`;
  }

  private _getUrlForId(id: number) {
    return `${this._getUrl()}/${id}`;
  }

  all(): Observable<Policy[]> {
    return (
      this.http
        .get<Policy[] | null>(this._getUrl())
        // TODO - This should be moved to backend and renewal days stored in config
        .pipe(map((policies) => _map(policies, PolicyHelpers.addPolicyStatus)))
    );
  }

  allForClient(clientId: number): Observable<Policy[]> {
    return this.http
      .get<Policy[]>(this._getUrlForClient(clientId))
      .pipe(map((policies) => _map(policies, PolicyHelpers.addPolicyStatus)));
  }

  allForClients(clientIds: number[]): Observable<Policy[]> {
    return forkJoin(
      _map(clientIds, (clientId) => this.allForClient(clientId))
    ).pipe(
      map((policies) => flatten(policies)),
      map((policies) => _map(policies, PolicyHelpers.addPolicyStatus))
    );
  }

  loadSeveral(policyIds: number[]): Observable<Policy[]> {
    return forkJoin(_map(policyIds, (clientId) => this.load(clientId))).pipe(
      map((policies) => flatten(policies)),
      map((policies) => _map(policies, PolicyHelpers.addPolicyStatus))
    );
  }

  recentlyViewed(): Observable<Policy[]> {
    return (
      this.http
        // #TODO fix on API!
        .get<Policy[] | null>(this._getRecentlyViewedUrl())
        .pipe(map((policies) => _map(policies, PolicyHelpers.addPolicyStatus)))
    );
  }

  load(id: number): Observable<Policy> {
    return this.http
      .get<Policy>(this._getUrlForId(id))
      .pipe(map(PolicyHelpers.addPolicyStatus));
  }

  create(policy: PolicyCreate): Observable<Policy> {
    return this.http
      .post<Policy>(this._getUrl(), policy)
      .pipe(map(PolicyHelpers.addPolicyStatus));
  }

  update(policy: PolicyUpdate): Observable<Policy> {
    return this.http
      .patch<Policy>(this._getUrl(), policy)
      .pipe(map(PolicyHelpers.addPolicyStatus));
  }

  delete(id: number): Observable<null> {
    return this.http.delete<null>(this._getUrlForId(id));
  }

  restore(id: number): Observable<null> {
    return this.http.put<null>(`${this._getUrl()}/restore/${id}`, null);
  }

  mta(policyId: number): Observable<Proposal> {
    return this.http.put<Proposal>(`${this._getUrl()}/${policyId}/mta`, null);
  }
}
